let cookieExpiry = null;
let cachedBearerToken = null;
let bearerTokenExpiry = null;

const fetchBearerTokenFromApigee = async () => {
    const now = Date.now();
    if (cachedBearerToken && bearerTokenExpiry && bearerTokenExpiry > now + 60000) {
        console.log('[APIGEE] Bearer token cache hit, reusing existing token');
        return cachedBearerToken;
    }

    console.log('[APIGEE] Bearer token cache miss — fetching new token');
    console.log('[APIGEE] Token URL:', process.env.REACT_APP_APIGEE_TOKEN_URL);
    console.log('[APIGEE] Client ID present:', !!process.env.REACT_APP_APIGEE_CLIENT_ID);
    console.log('[APIGEE] Client secret present:', !!process.env.REACT_APP_APIGEE_CLIENT_SECRET);

    const credentials = btoa(
        `${process.env.REACT_APP_APIGEE_CLIENT_ID}:${process.env.REACT_APP_APIGEE_CLIENT_SECRET}`
    );

    let tokenResponse;
    try {
        tokenResponse = await fetch(process.env.REACT_APP_APIGEE_TOKEN_URL, {
            method: 'POST',
            headers: {
                'Authorization': `Basic ${credentials}`,
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: 'grant_type=client_credentials'
        });
    } catch (err) {
        console.error('[APIGEE] Network error fetching bearer token:', err);
        throw err;
    }

    console.log('[APIGEE] Token endpoint response status:', tokenResponse.status);

    if (!tokenResponse.ok) {
        const body = await tokenResponse.text().catch(() => '(unreadable)');
        console.error('[APIGEE] Bearer token fetch failed. Status:', tokenResponse.status, '| Body:', body);
        throw new Error(`APIGEE bearer token fetch failed: ${tokenResponse.status}`);
    }

    const tokenData = await tokenResponse.json();
    cachedBearerToken = tokenData.access_token;
    bearerTokenExpiry = now + (parseInt(tokenData.expires_in, 10) * 1000) - 300000;
    console.log('[APIGEE] Bearer token acquired. Expires in (s):', tokenData.expires_in);
    return cachedBearerToken;
};

export const getApigeeBearerToken = async () => {
    console.log('[APIGEE] getApigeeBearerToken called');
    const token = await fetchBearerTokenFromApigee();
    console.log('[APIGEE] getApigeeBearerToken returning token:', token ? token.substring(0, 20) + '...' : 'null');
    return token;
};

/**
 * Ensures the APIGEE session cookie is set in the browser's cookie store.
 * Uses a two-step flow:
 * 1. POST to token URL with Basic Auth (client_id:client_secret) → bearer token
 * 2. GET cookie endpoint with Bearer token
 *    → APIGEE responds with Set-Cookie: apigee_session=<value>
 *    → Browser automatically stores and sends the cookie on subsequent
 *      requests and WebSocket connections to the same domain
 *
 * All configuration comes from environment variables:
 *   REACT_APP_APIGEE_TOKEN_URL     - OAuth token endpoint
 *   REACT_APP_APIGEE_CLIENT_ID     - client ID for Basic Auth
 *   REACT_APP_APIGEE_CLIENT_SECRET - client secret for Basic Auth
 *   REACT_APP_APIGEE_COOKIE_URL    - cookie endpoint (optional, has default)
 */
export const getApigeeSessionToken = async () => {
    console.log('[APIGEE] getApigeeSessionToken called');
    const now = Date.now();

    if (cookieExpiry && cookieExpiry > now + 60000) {
        console.log('[APIGEE] Session cookie still valid, skipping refresh. Expires in (ms):', cookieExpiry - now);
        return;
    }

    console.log('[APIGEE] Session cookie expired or not set — fetching new cookie');

    const bearerToken = await fetchBearerTokenFromApigee();
    console.log('[APIGEE] Bearer token ready for cookie fetch');

    const apigeeCookieUrl = process.env.REACT_APP_APIGEE_COOKIE_URL
        || 'https://dev.apigwx-aws.qualcomm.com/modem/v1/wbc/bootstrap-get-cookie';

    console.log('[APIGEE] Fetching session cookie from:', apigeeCookieUrl);

    let cookieResponse;
    try {
        cookieResponse = await fetch(apigeeCookieUrl, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${bearerToken}` }
        });
    } catch (err) {
        console.error('[APIGEE] Network error fetching session cookie:', err);
        throw err;
    }

    console.log('[APIGEE] Cookie endpoint response status:', cookieResponse.status);

    if (!cookieResponse.ok) {
        const body = await cookieResponse.text().catch(() => '(unreadable)');
        console.error('[APIGEE] Cookie fetch failed. Status:', cookieResponse.status, '| Body:', body);
        throw new Error(`APIGEE cookie fetch failed: ${cookieResponse.status}`);
    }

    cookieExpiry = bearerTokenExpiry;
    console.log('[APIGEE] Session cookie successfully set in browser cookie store');
};
